import styled, { css } from 'styled-components'
import { ThumbZoneActionsProps } from './ThumbZoneActions.types'

type StyledProps = Pick<
  ThumbZoneActionsProps,
  'position' | 'mobileOnly' | 'align' | 'direction' | 'showBorder' | 'blur'
>

const getJustifyContent = (align: ThumbZoneActionsProps['align']) => {
  switch (align) {
    case 'start':
      return 'flex-start'
    case 'center':
      return 'center'
    case 'end':
      return 'flex-end'
    case 'space-between':
      return 'space-between'
    case 'stretch':
    default:
      return 'stretch'
  }
}

const thumbZoneStyles = css<StyledProps>`
  display: flex;
  flex-direction: ${({ direction }) => direction || 'row'};
  justify-content: ${({ align }) => getJustifyContent(align)};
  align-items: ${({ align }) => (align === 'stretch' ? 'stretch' : 'center')};
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.default};
  padding-bottom: calc(
    ${({ theme }) => theme.spacing.default} + env(safe-area-inset-bottom, 0)
  );
  background: ${({ blur, theme }) =>
    blur
      ? `rgba(${
          (theme as { mode?: string }).mode === 'dark'
            ? '0, 0, 0'
            : '255, 255, 255'
        }, 0.9)`
      : theme.colors.background.primary};
  backdrop-filter: ${({ blur }) => (blur ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${({ blur }) => (blur ? 'blur(10px)' : 'none')};
  border-top: ${({ showBorder, theme }) =>
    showBorder ? `1px solid ${theme.colors.border.primary}` : 'none'};

  /* Ensure buttons stretch when align is stretch */
  ${({ align }) =>
    align === 'stretch' &&
    css`
      > * {
        flex: 1;
      }
    `}
`

const fixedStyles = css`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.sticky};
`

const stickyStyles = css`
  position: sticky;
  bottom: 0;
  z-index: 10;
`

export const Container = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      'position',
      'mobileOnly',
      'align',
      'direction',
      'showBorder',
      'blur',
    ].includes(prop),
})<StyledProps>`
  /* Mobile-only mode: Only apply thumb-zone positioning on mobile */
  ${({ mobileOnly, position }) =>
    mobileOnly
      ? css`
          /* Desktop: Render inline */
          @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
            display: flex;
            gap: ${({ theme }) => theme.spacing.small};
          }

          /* Mobile: Apply thumb-zone positioning */
          @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
            ${thumbZoneStyles}
            ${position === 'fixed' && fixedStyles}
            ${position === 'sticky' && stickyStyles}
          }
        `
      : css`
          /* Always apply thumb-zone positioning */
          ${thumbZoneStyles}
          ${position === 'fixed' && fixedStyles}
          ${position === 'sticky' && stickyStyles}
        `}
`

/**
 * Spacer component to prevent content from being hidden behind fixed thumb-zone actions.
 * Add this at the end of your content when using position='fixed'.
 */
export const ThumbZoneSpacer = styled.div<{ $mobileOnly?: boolean }>`
  ${({ $mobileOnly }) =>
    $mobileOnly
      ? css`
          /* Desktop: No spacer needed */
          @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
            display: none;
          }

          /* Mobile: Add spacing */
          @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
            height: calc(
              64px + env(safe-area-inset-bottom, 0)
            ); /* Approximate height of thumb zone */
          }
        `
      : css`
          height: calc(64px + env(safe-area-inset-bottom, 0));
        `}
`
